import { generateBetItemId } from '../utils/ticketHelpers';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { BetItem } from './BettingSlipDrawer';
import { getLiveEvents } from '../api/api';
import { EventSnapshot, Market, Outcome } from '../api/types';
import { OddsButton } from './OddsButton';
import { 
  find1x2Market, 
  findRestzeitGeneric, 
  findOUGeneric, 
  findNextGoal, 
  findHandicap, 
  findBTTSGeneric, 
  findDCGeneric, 
  findH2H,
  find1stHalfMarket,
  getMarketAttr,
  formatTippLabelWithAttr,
  isFilterAvailable,
  getMarketOptionsForSport,
  getLayoutGroupForMarketId
} from '../utils/marketParsers';
import { useTranslation } from '../i18n';
import { translateMarketFilterLabel, formatOddsButtonLabel } from '../utils/marketTranslations';
import { mergeSnapshotWithPatch, applyStreamUpdates } from '../utils/streamMerge';
import { liveStreamManager } from '../services/CatalogStreamManager';
import { useActiveStreams } from '../hooks/useActiveStreams';

import { SPORT_EMOJI } from './widgets/DesktopLeftSidebar';

const LockIcon = () => (
  <svg className="w-4 h-4 text-gray-400 block" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
  </svg>
);

function formatOdds(odds: string | number): string {
  const parsed = parseFloat(String(odds));
  return isNaN(parsed) ? '-' : parsed.toFixed(2).replace('.', ',');
}

function formatClock(ev: EventSnapshot, t?: (k: string) => string): string {
  const sport = ev.event?.sport_name;

  const sportId = ev.event?.sport_id;

  if (sport === 'Baseball' || sportId === 'sr:sport:3') {
    const text = ev.status?.match_status_text || '';
    const numMatch = text.match(/\d+/);
    if (numMatch) {
      return `${numMatch[0]}/9`;
    }
    if (text.toLowerCase().includes('break')) {
      const ps = ev.status?.period_scores;
      if (ps && ps.length > 0) {
        return `${ps.length}/9`;
      }
      return 'Break';
    }
    if (text) return text;
  }

  const useStatusText = sport === 'Basketball' || sport === 'Tennis' || sport === 'Table Tennis' || sport === 'Volleyball' || sportId === 'sr:sport:2' || sportId === 'sr:sport:5' || sportId === 'sr:sport:20' || sportId === 'sr:sport:23';

  if (useStatusText && ev.status?.match_status_text) {
    return ev.status.match_status_text.replace(/\b(?:1st|first)\b/gi, '1.').replace(/\b(?:2nd|second)\b/gi, '2.').replace(/\b(?:3rd|third)\b/gi, '3.').replace(/\b(?:4th|fourth)\b/gi, '4.').replace(/\b(\d+)(st|nd|rd|th)\b/gi, '$1.');
  }

  const stLower = (ev.status?.match_status_text || '').toLowerCase();
  if (stLower === 'halftime' || stLower === 'halbzeit' || stLower === 'devre arası' || stLower === 'ht') {
    return t ? t('live.ht') : 'HT';
  }
  if (stLower === 'ended' || stLower === 'closed' || stLower === 'finished' || stLower === 'ft' || stLower === 'beendet' || stLower === 'bitti' || stLower === 'ms') {
    return t ? t('live.ft') : 'FT';
  }

  if (ev.status?.clock) {
    const minStr = ev.status.clock.split(':')[0];
    if (minStr.includes('+')) {
      return minStr + "'";
    }
    const minNum = parseInt(minStr, 10);
    if (!isNaN(minNum)) {
      const displayMin = minNum + 1;
      
      if (sport === 'Soccer' || sportId === 'sr:sport:1') {
        const matchStatusCode = (ev.status as any)?.match_status;
        const isFirstHalf = stLower.includes('1.') || stLower.includes('1st') || stLower.includes('first') || stLower.includes('ilk') || stLower === '1' || matchStatusCode === '6' || matchStatusCode === 6;
        const isExtraTime = stLower.includes('et') || stLower.includes('extra') || stLower.includes('uzatma') || stLower.includes('verläng') || stLower.includes('verlaeng') || stLower.includes('overtime') || matchStatusCode === '41' || matchStatusCode === '42' || matchStatusCode === 41 || matchStatusCode === 42;

        const is90 = minStr === '90';
        const is45 = minStr === '45';

        if ((is90 && !isExtraTime && !isFirstHalf) || (is45 && isFirstHalf)) {
          const baseMin = is90 ? 90 : 45;
          // @ts-ignore: stoppage_time exists in SSE payload
          const stoppage = ev.status?.stoppage_time;
          if (stoppage && stoppage !== '0:00' && typeof stoppage === 'string') {
            const parts = stoppage.split(':');
            if (parts.length === 2) {
              const sMin = parseInt(parts[0], 10);
              const sSec = parseInt(parts[1], 10);
              if (!isNaN(sMin) && !isNaN(sSec)) {
                let added = sMin;
                if (sSec > 0 || sMin === 0) added += 1;
                return `${baseMin}+${added}'`;
              }
            }
          }
          return `${baseMin}'`;
        }

        if (isFirstHalf && displayMin > 45 && !isExtraTime) {
          return `45+${displayMin - 45}'`;
        }
        if (displayMin > 90 && !isExtraTime && !isFirstHalf) {
          return `90+${displayMin - 90}'`;
        }
      }
      
      return displayMin + "'";
    }
    return minStr + "'";
  }
  if (ev.status?.match_status_text) {
    return ev.status.match_status_text.replace(/\b(?:1st|first)\b/gi, '1.').replace(/\b(?:2nd|second)\b/gi, '2.').replace(/\b(?:3rd|third)\b/gi, '3.').replace(/\b(?:4th|fourth)\b/gi, '4.').replace(/\b(\d+)(st|nd|rd|th)\b/gi, '$1.');
  }
  return 'Live';
}

interface MobileLiveSectionProps {
  bets: BetItem[];
  onAddBet: (bet: BetItem) => void;
  onRemoveBet: (id: string) => void;
}

export const MobileLiveSection: React.FC<MobileLiveSectionProps> = ({
  bets,
  onAddBet,
  onRemoveBet,
}) => {
  const { locale, t } = useTranslation();
  const navigate = useNavigate();
  const [liveEvents, setLiveEvents] = useState<EventSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSport, setActiveSport] = useState('Soccer');
  const [activeBetFilter, setActiveBetFilter] = useState<string | number>('Tipp');
  const activeStreams = useActiveStreams();

  const handleEventClick = (ev: EventSnapshot) => {
    navigate('/match/' + ev.event_id, { state: { event: ev } });
  };

  const loadData = useCallback(async (options?: RequestInit) => {
    try {
      const live = await getLiveEvents(options);
      setLiveEvents(live.items);
    } catch (e) {
      if ((e as any)?.name !== 'AbortError') {
        console.error('Failed to load live events for MobileLiveSection', e);
      }
    } finally {
      if (!options?.signal?.aborted) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    loadData({ signal: abortController.signal });

    let isLiveFirst = true;
    const unsubLive = liveStreamManager.subscribe((upserts, deletes) => {
      if (abortController.signal.aborted) return;
      if (isLiveFirst) {
        isLiveFirst = false;
      } else {
        if (upserts.length > 0 || deletes.length > 0) {
          const applyUpdates = applyStreamUpdates(upserts, deletes, false);
          setLiveEvents(prev => applyUpdates(prev) || []);
        }
      }
    });

    return () => {
      abortController.abort();
      unsubLive();
    };
  }, [loadData]);

  // Extract unique sports
  const sportTabs = useMemo(() => {
    const counts: Record<string, number> = {};
    const names: Record<string, string> = {};
    for (const ev of liveEvents) {
      const sId = ev.event.sport_id;
      const sName = ev.event.sport_name;
      if (sId && sName) {
        counts[sId] = (counts[sId] || 0) + 1;
        names[sId] = sName;
      }
    }

    const SPORT_ORDER_BY_ID: Record<string, number> = {
      'sr:sport:1': 1, // Soccer
      'sr:sport:2': 2, // Basketball
      'sr:sport:5': 3, // Tennis
      'sr:sport:4': 4, // Ice Hockey
      'sr:sport:23': 5, // Volleyball
      'sr:sport:6': 6, // Handball
      'sr:sport:20': 7 // Table Tennis
    };

    return Object.entries(counts)
      .filter(([id]) => id in SPORT_ORDER_BY_ID)
      .sort((a, b) => (SPORT_ORDER_BY_ID[a[0]] ?? 999) - (SPORT_ORDER_BY_ID[b[0]] ?? 999))
      .slice(0, 5)
      .map(([id, count]) => ({ name: names[id], count, emoji: SPORT_EMOJI[names[id]] || '🏅' }));
  }, [liveEvents]);

  // Fallback active sport if selected activeSport has no events and there are sports
  useEffect(() => {
    if (sportTabs.length > 0) {
      const exists = sportTabs.some((t: { name: string }) => t.name === activeSport);
      if (!exists) {
        setActiveSport(sportTabs[0].name);
      }
    }
  }, [sportTabs, activeSport]);

  const filteredEvents = liveEvents.filter((ev) => ev.event.sport_name === activeSport);
  const liveSportFilters = getMarketOptionsForSport(activeSport, true);
  const availableFilters = liveSportFilters.filter((f) => filteredEvents.some((ev) => isFilterAvailable(f, ev.markets)));
  const displayedEvents = filteredEvents.filter((ev) => isFilterAvailable(activeBetFilter, ev.markets)).slice(0, 8);

  useEffect(() => {
    const available = liveSportFilters.filter((f) => filteredEvents.some((ev) => isFilterAvailable(f, ev.markets)));
    if (available.length > 0) {
      if (!available.includes(activeBetFilter)) {
        setActiveBetFilter(available[0]);
      }
    } else if (liveSportFilters.length > 0 && !liveSportFilters.includes(activeBetFilter)) {
      setActiveBetFilter(liveSportFilters[0]);
    }
  }, [filteredEvents, activeBetFilter, activeSport, liveSportFilters]);

  const isSelected = (eventId: string, marketId: string, outcomeId: string, specifiers?: Record<string, string>) =>
    bets.some((b) => b.id === generateBetItemId(eventId, marketId, outcomeId, specifiers));

  const handleOddsClick = (ev: EventSnapshot, market: Market, outcome: Outcome, tippLabel: string) => {
    const id = generateBetItemId(ev.event_id, market.market_id, outcome.outcome_id, market.specifiers);
    if (isSelected(ev.event_id, market.market_id, outcome.outcome_id, market.specifiers)) {
      onRemoveBet(id);
    } else {

      const formattedTipp = formatTippLabelWithAttr(tippLabel, market);
      onAddBet({
        id,
        matchId: 0,
        matchName: `${ev.event.home.name} - ${ev.event.away.name}`,
        betType: tippLabel === 'Heimsieg' ? 'home' : tippLabel === 'Unentschieden' ? 'draw' : 'away',
        tippLabel: formattedTipp,
        odds: formatOdds(outcome.odds),
        eventId: ev.event_id,
        marketId: market.market_id,
        outcomeId: outcome.outcome_id,
        outcomeName: (outcome as any).player_name || (outcome as any).playerName || outcome.name,
        specifiers: market.specifiers
          ? typeof market.specifiers === 'string'
            ? market.specifiers
            : Object.entries(market.specifiers).map(([k, v]) => `${k}=${v}`).join('|')
          : '',
      });
    }
  };

  if (loading) {
    return (
      <section className="px-3 py-2">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-base font-black text-gray-900">Live Wetten</h2>
        </div>
        <div className="flex justify-center items-center h-24 bg-white rounded-2xl shadow-sm">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600" />
        </div>
      </section>
    );
  }

  if (liveEvents.length === 0) {
    return null;
  }

  const renderOddsButtons = (ev: EventSnapshot, items: { label: string; outcome: Outcome; market: Market; tipp: string }[]) => (
    <div className="flex gap-2 flex-1">
      {items.map(({ label, outcome, market, tipp }) => {
        const sel = isSelected(ev.event_id, market.market_id, outcome.outcome_id, market.specifiers);
        const oddsVal = formatOdds(outcome.odds);
        const disabled = !outcome.active || oddsVal === '-' || parseFloat(String(outcome.odds)) <= 1;
        return (
          <OddsButton
            odds={outcome.odds}
            selected={sel}
            key={label}
            onClick={() => handleOddsClick(ev, market, outcome, tipp)}
            disabled={disabled}
            className={`flex-1 flex items-center justify-between rounded px-2 py-1.5 transition min-w-0 w-full border ${sel ? 'bg-[#333] border-[#333]' : disabled ? 'bg-gray-50 opacity-40 cursor-not-allowed border-gray-200' : 'bg-[#e3e6eb] border-[#c0c5cc] hover:bg-[#d8dce0]'}`}
          >
            <span className={`text-[12px] font-medium truncate ${sel ? 'text-[#eee]' : 'text-[#555]'}`}>
              {formatOddsButtonLabel(label, ev.event.home.name, ev.event.away.name, locale)}
            </span>
            <span className={`text-[14px] font-bold ${sel ? 'text-white' : 'text-gray-900'}`}>
              {disabled ? <LockIcon /> : oddsVal}
            </span>
          </OddsButton>
        );
      })}
    </div>
  );

  return (
    <section className="px-3 py-4">
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-red-600 animate-pulse inline-block" />
          <h2 className="text-base font-black text-gray-900">Live Wetten</h2>
        </div>
        <button onClick={() => navigate('/live')} className="flex items-center gap-1 text-sm font-semibold text-gray-700">
          Alle
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {sportTabs.length > 0 && (
        <div className="bg-white rounded-2xl p-3 shadow-sm border border-gray-100 flex gap-4 overflow-x-auto scrollbar-hide mb-3">
          {sportTabs.map((sport: { name: string; count: number; emoji: string }) => {
            const active = activeSport === sport.name;
            return (
              <button
                key={sport.name}
                onClick={() => setActiveSport(sport.name)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl transition whitespace-nowrap text-xs font-semibold ${
                  active ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-gray-50 text-gray-600 border border-transparent'
                }`}
              >
                <span>{sport.emoji}</span>
                <span>{sport.name === 'Soccer' ? 'Fußball' : sport.name === 'eSoccer' ? 'eFootball' : sport.name}</span>
                <span className={`text-[10px] px-1 rounded-full ${active ? 'bg-red-200 text-red-700' : 'bg-gray-200 text-gray-600'}`}>{sport.count}</span>
              </button>
            );
          })}
        </div>
      )}

      {availableFilters.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {availableFilters.map((f) => (
            <button
              key={f}
              onClick={() => setActiveBetFilter(f)}
              className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-bold border transition ${
                activeBetFilter === f
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-700 border-gray-300'
              }`}
            >
              {translateMarketFilterLabel(f, locale)}
            </button>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 mt-2">
        {displayedEvents.length === 0 ? (
          <div className="p-6 text-center text-gray-400 text-sm">
            {filteredEvents.length === 0 ? "Bu spor için canlı maç yok." : "Bu market için canlı maç yok."}
          </div>
        ) : (
          displayedEvents.map((ev, idx) => {
            const redHome = (ev.status?.statistics?.red_cards?.home || 0) > 0;
            const redAway = (ev.status?.statistics?.red_cards?.away || 0) > 0;

            return (
              <div key={ev.event_id}>
                {idx > 0 && <div className="border-t border-gray-100 mx-4" />}
                <div className="px-4 py-3">
                  <div className="flex mb-3 cursor-pointer" onClick={() => handleEventClick(ev)}>
                    <div className="text-left min-w-[45px] flex-shrink-0 pt-0.5">
                      <span className="text-xs font-bold text-red-600 leading-none">{formatClock(ev, t)}</span>
                    </div>
                    <div className="flex-1 min-w-0 flex items-center">
                      <div className="flex-1 min-w-0 flex flex-col justify-center">
                        <div className="flex items-center gap-1.5 truncate h-[22px]">
                          <img src={ev.event.home.crest_url || '/default-crest.svg'} alt="" className="w-5 h-5 object-contain flex-shrink-0 rounded-[4px]" onError={(e) => { (e.target as HTMLImageElement).src = '/default-crest.svg'; }} />
                          <span className="text-sm font-semibold text-gray-900 truncate">{ev.event.home.name}</span>
                          {redHome && <span className="w-2.5 h-3 bg-red-600 rounded-sm inline-block" />}
                        </div>
                        <div className="flex items-center gap-1.5 truncate h-[22px]">
                          <img src={ev.event.away.crest_url || '/default-crest.svg'} alt="" className="w-5 h-5 object-contain flex-shrink-0 rounded-[4px]" onError={(e) => { (e.target as HTMLImageElement).src = '/default-crest.svg'; }} />
                          <span className="text-sm font-semibold text-gray-900 truncate">{ev.event.away.name}</span>
                          {redAway && <span className="w-2.5 h-3 bg-red-600 rounded-sm inline-block" />}
                        </div>
                      </div>

                      {activeStreams.has(ev.event_id) && (
                        <div className="mx-2 flex-shrink-0">
                          <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}

                      <div className="flex flex-col justify-center flex-shrink-0 text-right">
                        <div className="h-[22px] flex items-center justify-end">
                          <span className="text-sm font-bold text-gray-900 ml-2">{ev.status?.home_score ?? '0'}</span>
                        </div>
                        <div className="h-[22px] flex items-center justify-end">
                          <span className="text-sm font-bold text-gray-900 ml-2">{ev.status?.away_score ?? '0'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <div className="flex gap-2 items-center">
                      {(() => {
                        const layoutGrp = getLayoutGroupForMarketId(activeBetFilter);
                        let attrMarket: Market | undefined;
                        if (layoutGrp === 'Tipp') { const r = find1x2Market(ev.markets); attrMarket = r?.market; }
                        else if (layoutGrp === 'Tipp Restzeit') { const r = findRestzeitGeneric(ev.markets); attrMarket = r?.market; }
                        else if (layoutGrp === 'Über/Unter') { const r = findOUGeneric(ev.markets, false, ev); attrMarket = r?.market; }
                        else if (layoutGrp === 'Nächstes Tor') { const r = findNextGoal(ev.markets); attrMarket = r?.market; }
                        else if (layoutGrp === 'Handicap' || layoutGrp === 'Satz-Handicap') { const r = findHandicap(ev.markets); attrMarket = r?.market; }
                        else if (layoutGrp === 'Beide Teams treffen') { const r = findBTTSGeneric(ev.markets); attrMarket = r?.market; }
                        else if (layoutGrp === 'Halbzeit-Über/Unter') { const r = findOUGeneric(ev.markets, true, ev); attrMarket = r?.market; }
                        else if (layoutGrp === 'Doppelte Chance') { const r = findDCGeneric(ev.markets); attrMarket = r?.market; }
                        else if (layoutGrp === 'Head-To-Head' || layoutGrp === 'Sieger' || layoutGrp === 'Draw No Bet') { const r = findH2H(ev.markets); attrMarket = r?.market; }
                        else if (layoutGrp === '1. Halbzeit') { const r = find1stHalfMarket(ev.markets); attrMarket = r?.market; }
                        if (!attrMarket) return null;
                        const attr = getMarketAttr(attrMarket);
                        if (!attr) return null;
                        return <span className="text-[10px] font-bold text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">{attr}</span>;
                      })()}

                      {(() => {
                        const layoutGrp = getLayoutGroupForMarketId(activeBetFilter);
                        if (layoutGrp === 'Tipp') {
                          const m = find1x2Market(ev.markets);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2,3].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons(ev, [
                            { label: '1', outcome: m.home, market: m.market, tipp: 'Heimsieg' },
                            { label: 'X', outcome: m.draw, market: m.market, tipp: 'Unentschieden' },
                            { label: '2', outcome: m.away, market: m.market, tipp: 'Auswärtssieg' },
                          ]);
                        }
                        if (layoutGrp === 'Tipp Restzeit') {
                          const m = findRestzeitGeneric(ev.markets);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2,3].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons(ev, [
                            { label: '1', outcome: m.home, market: m.market, tipp: 'Restzeit Heimsieg' },
                            { label: 'X', outcome: m.draw, market: m.market, tipp: 'Restzeit Unentschieden' },
                            { label: '2', outcome: m.away, market: m.market, tipp: 'Restzeit Auswärtssieg' },
                          ]);
                        }
                        if (layoutGrp === 'Über/Unter') {
                          const m = findOUGeneric(ev.markets, false, ev);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons(ev, [
                            { label: 'Über', outcome: m.over, market: m.market, tipp: 'Über' },
                            { label: 'Unter', outcome: m.under, market: m.market, tipp: 'Unter' },
                          ]);
                        }
                        if (layoutGrp === 'Nächstes Tor') {
                          const m = findNextGoal(ev.markets);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2,3].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons(ev, [
                            { label: '1', outcome: m.home, market: m.market, tipp: 'Nächstes Tor Heim' },
                            { label: 'Kein Tor', outcome: m.none, market: m.market, tipp: 'Kein Tor' },
                            { label: '2', outcome: m.away, market: m.market, tipp: 'Nächstes Tor Auswärts' },
                          ]);
                        }
                        if (layoutGrp === 'Handicap' || layoutGrp === 'Satz-Handicap') {
                          const m = findHandicap(ev.markets);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          const buttons = [
                            { label: '1', outcome: m.o1, market: m.market, tipp: 'Handicap Heim' },
                          ];
                          if (m.o3) {
                            buttons.push({ label: 'X', outcome: m.o3, market: m.market, tipp: 'Handicap Unentschieden' });
                          }
                          buttons.push({ label: '2', outcome: m.o2, market: m.market, tipp: 'Handicap Auswärts' });
                          return renderOddsButtons(ev, buttons);
                        }
                        if (layoutGrp === 'Beide Teams treffen') {
                          const m = findBTTSGeneric(ev.markets);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons(ev, [
                            { label: 'Ja', outcome: m.yes, market: m.market, tipp: 'Ja' },
                            { label: 'Nein', outcome: m.no, market: m.market, tipp: 'Nein' },
                          ]);
                        }
                        if (layoutGrp === 'Halbzeit-Über/Unter') {
                          const m = findOUGeneric(ev.markets, true, ev);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons(ev, [
                            { label: 'Über', outcome: m.over, market: m.market, tipp: '1. Halbzeit Über' },
                            { label: 'Unter', outcome: m.under, market: m.market, tipp: '1. Halbzeit Unter' },
                          ]);
                        }
                        if (layoutGrp === 'Doppelte Chance') {
                          const m = findDCGeneric(ev.markets);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2,3].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons(ev, [
                            { label: '1X', outcome: m.o1x, market: m.market, tipp: '1X' },
                            { label: '12', outcome: m.o12, market: m.market, tipp: '12' },
                            { label: 'X2', outcome: m.ox2, market: m.market, tipp: 'X2' },
                          ]);
                        }
                        if (layoutGrp === 'Head-To-Head' || layoutGrp === 'Sieger' || layoutGrp === 'Draw No Bet') {
                          const m = findH2H(ev.markets);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons(ev, [
                            { label: '1', outcome: m.home, market: m.market, tipp: 'Sieger Heim' },
                            { label: '2', outcome: m.away, market: m.market, tipp: 'Sieger Auswärts' },
                          ]);
                        }
                        if (layoutGrp === '1. Halbzeit') {
                          const m = find1stHalfMarket(ev.markets);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2,3].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons(ev, [
                            { label: '1', outcome: m.home, market: m.market, tipp: '1. Halbzeit Heim' },
                            { label: 'X', outcome: m.draw, market: m.market, tipp: '1. Halbzeit Unentschieden' },
                            { label: '2', outcome: m.away, market: m.market, tipp: '1. Halbzeit Auswärts' },
                          ]);
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
};
